import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Link2,
  Unlink,
  RefreshCw,
  CheckCircle,
  ExternalLink,
  Loader2,
  Lock,
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = {
  linkedin: {
    name: "LinkedIn Marketing",
    icon: <FaLinkedin className="w-6 h-6 text-[#0A66C2]" />,
    color: "bg-[#0A66C2]/10 border-[#0A66C2]/30",
    description: "Connect your LinkedIn Campaign Manager",
    enabled: true,
  },
  meta: {
    name: "Meta (Facebook)",
    icon: <FaMeta className="w-6 h-6 text-[#1877F2]" />,
    color: "bg-[#1877F2]/10 border-[#1877F2]/30",
    description: "Connect your Facebook & Instagram Ads account",
    enabled: true,
    hidden: true,
  },
  google: {
    name: "Google Ads",
    icon: <FcGoogle className="w-6 h-6" />,
    color: "bg-white/10 border-white/30",
    description: "Connect your Google Ads account",
    enabled: true,
  },
  bing: {
    name: "Microsoft Advertising",
    icon: <FaMicrosoft className="w-6 h-6 text-[#00A4EF]" />,
    color: "bg-[#00A4EF]/10 border-[#00A4EF]/30",
    description: "Connect your Microsoft (Bing) Ads account",
    enabled: true,
  },
};

export function PlatformConnections({ onConnectionChange, user }) {
  const [connections, setConnections] = useState({});
  const [oauthConfig, setOauthConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(null);

  const fetchConnections = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/connections`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        const map = {};
        data.forEach((c) => { map[c.platform] = c; });
        setConnections(map);
        if (onConnectionChange) onConnectionChange(map);
      }
    } catch (e) {
      logger.error("Failed to fetch connections:", e);
    } finally {
      setLoading(false);
    }
  }, [onConnectionChange]);

  const fetchOAuthConfig = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/oauth/config`, { credentials: "include" });
      if (resp.ok) setOauthConfig(await resp.json());
    } catch (e) {
      logger.error("Failed to fetch OAuth config:", e);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
    fetchOAuthConfig();
  }, [fetchConnections, fetchOAuthConfig]);

  const isDemoMode = user?.is_demo;

  const handleConnect = async (platform) => {
    const cfg = PLATFORMS[platform];
    if (!cfg?.enabled) {
      toast.info(`${cfg?.name || platform} integration coming soon.`);
      return;
    }

    if (isDemoMode) {
      setConnecting(platform);
      try {
        const resp = await fetch(`${API}/connect/demo/${platform}`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ account_name: `Demo ${cfg.name} Account` }),
        });
        if (resp.ok) {
          toast.success(`${cfg.name} connected! (Demo Mode)`);
          await fetchConnections();
        } else {
          toast.error("Failed to connect in demo mode");
        }
      } catch {
        toast.error("Failed to connect");
      } finally {
        setConnecting(null);
      }
      return;
    }

    setConnecting(platform);
    try {
      const resp = await fetch(`${API}/oauth/connect/${platform}`, { credentials: "include" });
      if (resp.ok) {
        const data = await resp.json();
        window.open(data.auth_url, "_blank");
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Failed to initiate connection");
        setConnecting(null);
      }
    } catch {
      toast.error("Failed to connect");
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    setDisconnecting(platform);
    try {
      const resp = await fetch(`${API}/oauth/disconnect/${platform}`, {
        method: "POST",
        credentials: "include",
      });
      if (resp.ok) {
        toast.success(`Disconnected from ${PLATFORMS[platform].name}`);
        await fetchConnections();
      } else {
        toast.error("Failed to disconnect");
      }
    } catch {
      toast.error("Failed to disconnect");
    } finally {
      setDisconnecting(null);
      setShowDisconnectDialog(null);
    }
  };

  const handleRefreshToken = async (platform) => {
    try {
      const resp = await fetch(`${API}/oauth/refresh/${platform}`, {
        method: "POST",
        credentials: "include",
      });
      if (resp.ok) {
        toast.success("Token refreshed");
        await fetchConnections();
      } else {
        toast.error("Refresh failed — please reconnect");
      }
    } catch {
      toast.error("Refresh failed");
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-6 flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="platform-connections" className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-violet-400" />
            Ad Platform Connections
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Connect your advertising accounts to start optimizing campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(PLATFORMS).filter(([_, cfg]) => !cfg.hidden).map(([platform, cfg], idx) => {
              const conn = connections[platform];
              const isConnected = conn?.status === "active";
              const isDemo = conn?.is_demo;
              const isConfigured = oauthConfig[platform]?.is_configured;
              const tokenExpired = conn?.token_expired;

              return (
                <motion.div
                  key={platform}
                  data-testid={`platform-card-${platform}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.08 }}
                  className={cn(
                    "p-4 rounded-xl border-2 transition-all relative",
                    isConnected ? cfg.color : "bg-white/5 border-white/10",
                    !cfg.enabled && !isConnected && "opacity-60"
                  )}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {cfg.icon}
                      <div>
                        <h3 className="text-white font-semibold">{cfg.name}</h3>
                        <p className="text-zinc-400 text-xs">{cfg.description}</p>
                      </div>
                    </div>
                    {isConnected && (
                      <div className="flex items-center gap-1">
                        {isDemo && (
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">Demo</Badge>
                        )}
                        {tokenExpired && !isDemo && (
                          <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">Expired</Badge>
                        )}
                        {!tokenExpired && !isDemo && (
                          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs">Live</Badge>
                        )}
                      </div>
                    )}
                    {!cfg.enabled && !isConnected && (
                      <Badge className="bg-zinc-700/50 text-zinc-400 border-zinc-600/30 text-xs">Coming Soon</Badge>
                    )}
                  </div>

                  {isConnected && (
                    <div className="mb-3 p-2 bg-black/30 rounded-lg">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        <span className="text-zinc-300">{conn.account_name || "Connected"}</span>
                      </div>
                      {conn.last_synced && (
                        <p className="text-zinc-500 text-xs mt-1 ml-6">
                          Last synced: {new Date(conn.last_synced).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {isConnected ? (
                      <>
                        {tokenExpired && !isDemo && (
                          <Button
                            data-testid={`refresh-${platform}-btn`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleRefreshToken(platform)}
                            className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                          </Button>
                        )}
                        <Button
                          data-testid={`disconnect-${platform}-btn`}
                          size="sm"
                          variant="outline"
                          onClick={() => setShowDisconnectDialog(platform)}
                          disabled={disconnecting === platform}
                          className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                        >
                          {disconnecting === platform ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Unlink className="w-3 h-3 mr-1" />
                          )}
                          Disconnect
                        </Button>
                      </>
                    ) : cfg.enabled && isConfigured ? (
                      <Button
                        data-testid={`connect-${platform}-btn`}
                        size="sm"
                        onClick={() => handleConnect(platform)}
                        disabled={connecting === platform}
                        className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white"
                      >
                        {connecting === platform ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <ExternalLink className="w-3 h-3 mr-1" />
                        )}
                        Connect
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled
                        className="bg-zinc-700/50 text-zinc-400 cursor-not-allowed"
                      >
                        <Lock className="w-3 h-3 mr-1" />
                        {cfg.enabled ? "Not Configured" : "Coming Soon"}
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!showDisconnectDialog} onOpenChange={() => setShowDisconnectDialog(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Disconnect {PLATFORMS[showDisconnectDialog]?.name}?</DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will remove access to your {PLATFORMS[showDisconnectDialog]?.name} advertising data.
              You can reconnect at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisconnectDialog(null)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-disconnect-btn"
              onClick={() => handleDisconnect(showDisconnectDialog)}
              disabled={disconnecting === showDisconnectDialog}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {disconnecting === showDisconnectDialog ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Unlink className="w-4 h-4 mr-2" />
              )}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
